import { axiosManager } from '@jooxter/api';
import { create, StateCreator } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { JooxterState } from './types';
import { fromISO, getMinBetweenNextHourAndEndOfDay, isDiffOneHour, now, toISO } from '@jooxter/utils';
import { FILTERS_STORAGE_TOKEN, STORAGE_VERSION } from './constants';
import { getEnvVar } from '@jooxter/env';
import { stateFactory, StoreEnum } from './state.factory';

const defaultState = (set: Parameters<StateCreator<JooxterState>>[0]): JooxterState => {
  return stateFactory(set, StoreEnum.default);
};

export const useDefaultStore = getEnvVar('DEV')
  ? create<JooxterState>()(
      devtools(
        persist((set) => defaultState(set), {
          name: FILTERS_STORAGE_TOKEN,
          partialize: (state) => ({
            filter: {
              resource: state.filter.resource,
              heatmap: state.filter.heatmap,
              colleague: Object.fromEntries(
                Object.entries(state.filter.colleague).filter(([key]) => !['date'].includes(key))
              ),
              realTime: state.filter.realTime,
              floorPlan: state.filter.floorPlan,
            },
            spaceSearchView: state.spaceSearchView,
            spaceSchedulerView: state.spaceSchedulerView,
            myBookingsView: state.myBookingsView,
            foldedFilters: state.foldedFilters,
            email: state.email,
            axiosInstance: state.axiosInstance,
          }),
          version: STORAGE_VERSION,
          onRehydrateStorage: () => {
            return (_state, error) => {
              if (error) {
                return;
              }

              const nowSnapshot = now();

              if (_state?.filter?.from) {
                const fromDateTime = fromISO(_state.filter.from);
                const toDateTime = fromISO(_state.filter.to);
                const isPast = fromDateTime < nowSnapshot;

                if (isPast) {
                  _state.setFilter({
                    ..._state.filter,
                    from: toISO(nowSnapshot),
                    to: isDiffOneHour(fromDateTime, toDateTime)
                      ? toISO(getMinBetweenNextHourAndEndOfDay(fromDateTime))
                      : _state.filter.to,
                  });
                }
              }

              if (_state?.filter?.resource.date.from) {
                const fromResourceFilterDateTime = fromISO(_state.filter.resource.date.from);
                const toResourceFilterDateTime = fromISO(_state.filter.resource.date.to);
                const isPastResourceFilterFrom = fromResourceFilterDateTime < nowSnapshot;
                const isPastResourceFilterTo = toResourceFilterDateTime < nowSnapshot;

                if (isPastResourceFilterFrom) {
                  _state.setFilter({
                    ..._state.filter,
                    resource: {
                      ..._state.filter.resource,
                      date: {
                        ..._state.filter.resource.date,
                        from: toISO(nowSnapshot),
                        to:
                          isDiffOneHour(fromResourceFilterDateTime, toResourceFilterDateTime) || isPastResourceFilterTo
                            ? toISO(getMinBetweenNextHourAndEndOfDay(nowSnapshot))
                            : _state.filter.resource.date.to,
                      },
                    },
                  });
                }
              }

              if (_state?.axiosInstance) {
                axiosManager.setInstance(_state.axiosInstance);
              }
            };
          },
        })
      )
    )
  : create<JooxterState>()(
      persist((set) => defaultState(set), {
        name: FILTERS_STORAGE_TOKEN,
        partialize: (state) => ({
          filter: {
            heatmap: state.filter.heatmap,
            resource: state.filter.resource,
            colleague: Object.fromEntries(
              Object.entries(state.filter.colleague).filter(([key]) => !['date'].includes(key))
            ),
            realTime: state.filter.realTime,
            floorPlan: state.filter.floorPlan,
          },
          spaceSearchView: state.spaceSearchView,
          spaceSchedulerView: state.spaceSchedulerView,
          myBookingsView: state.myBookingsView,
          foldedFilters: state.foldedFilters,
          email: state.email,
          axiosInstance: state.axiosInstance,
        }),
        version: STORAGE_VERSION,
        onRehydrateStorage: () => {
          return (_state, error) => {
            if (error) {
              return;
            }

            const nowSnapshot = now();

            if (_state?.filter?.from) {
              const fromDateTime = fromISO(_state.filter.from);
              const toDateTime = fromISO(_state.filter.to);
              const isPast = fromDateTime < nowSnapshot;

              if (isPast) {
                _state.setFilter({
                  ..._state.filter,
                  from: toISO(nowSnapshot),
                  to: isDiffOneHour(fromDateTime, toDateTime)
                    ? toISO(getMinBetweenNextHourAndEndOfDay(fromDateTime))
                    : _state.filter.to,
                });
              }
            }

            if (_state?.filter?.resource.date.from) {
              const fromResourceFilterDateTime = fromISO(_state.filter.resource.date.from);
              const toResourceFilterDateTime = fromISO(_state.filter.resource.date.to);
              const isPastResourceFilterFrom = fromResourceFilterDateTime < nowSnapshot;
              const isPastResourceFilterTo = toResourceFilterDateTime < nowSnapshot;

              if (isPastResourceFilterFrom) {
                _state.setFilter({
                  ..._state.filter,
                  resource: {
                    ..._state.filter.resource,
                    date: {
                      ..._state.filter.resource.date,
                      from: toISO(nowSnapshot),
                      to:
                        isDiffOneHour(fromResourceFilterDateTime, toResourceFilterDateTime) || isPastResourceFilterTo
                          ? toISO(getMinBetweenNextHourAndEndOfDay(nowSnapshot))
                          : _state.filter.resource.date.to,
                    },
                  },
                });
              }

              if (_state?.axiosInstance) {
                axiosManager.setInstance(_state.axiosInstance);
              }
            }
          };
        },
      })
    );
